import { Link, useParams } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import { 
  MdLocationPin,
  MdHome,
  MdWork,
  MdApartment,
  MdEdit,
  MdMyLocation
} from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "../services/ApiService";
import PageLoader from "../components/PageLoader";
import Header from "../components/Header";
import { loadGoogleMapsScript } from '../services/GoogleMap';
import AuthService from "../services/AuthService";

const AddAddress = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true); // Start with true until location is determined
  const [isGettingLocation, setIsGettingLocation] = useState(true); // Start with true
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [locationDetermined, setLocationDetermined] = useState(false);
  const authUser = AuthService.getCurrentUser();
  
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const { state } = useLocation();
  const address = state?.address || null;
  const searchParams = new URLSearchParams(window.location.search);
  const goto = searchParams.get('goto') || '';
  
  const [formData, setFormData] = useState({
    type: "home",
    label: "",
    address: "",
    villa_apt_no: "",
    apartment: "",
    floor: "",
    street: "",
    area: "",
    city: "Dubai",
    country: "UAE",
    notes: "",
    is_default: false,
    lat: 25.276987,
    lng: 55.296249
  });

  // Get user's current location
  const getUserCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      setLocationPermissionDenied(true);
      setIsGettingLocation(false);
      setIsPageLoading(false);
      return;
    }

    setIsGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        // Update form data with current location
        setFormData(prev => ({
          ...prev,
          lat: latitude,
          lng: longitude
        }));

        setLocationDetermined(true);
        setIsGettingLocation(false);
        setIsPageLoading(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Unable to get your location";
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location access denied. Please enable location services in your browser settings";
            setLocationPermissionDenied(true);
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out.";
            break;
        }
        
        toast.error(errorMessage);
        setIsGettingLocation(false);
        setIsPageLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  // Load address data if editing, otherwise get current location
  useEffect(() => {
    if (address) {
      setFormData(address);
      setIsPageLoading(false);
      setIsGettingLocation(false);
      setLocationDetermined(true);
    } else {
      // Automatically get current location when component mounts
      getUserCurrentLocation();
    }
  }, []);

  // Initialize map and autocomplete only after location is determined
  useEffect(() => {
    if (!locationDetermined) return;
    
    let isMounted = true;
    let autocompleteInstance = null;

    const initMap = async () => {
      try {
        // Load Google Maps script if needed
        if (!window.google?.maps) {
          await loadGoogleMapsScript(process.env.REACT_APP_MAP_KEY);
        }

        if (!isMounted || !mapRef.current) return;

        // Create map centered on current location or default
        const center = { lat: formData.lat, lng: formData.lng };
        const newMap = new window.google.maps.Map(mapRef.current, {
          center: center,
          zoom: 15,
          disableDefaultUI: true
        });

        // Create marker
        const newMarker = new window.google.maps.Marker({
          position: center,
          map: newMap,
          draggable: true,
          title: "Your location"
        });

        // Handle marker drag end
        newMarker.addListener("dragend", (e) => {
          const newPos = e.latLng.toJSON();
          setFormData(prev => ({
            ...prev,
            lat: newPos.lat,
            lng: newPos.lng
          }));
          reverseGeocode(newPos);
        });

        // Initialize autocomplete
        const addressInput = document.getElementById("address-input");
        if (addressInput) {
          autocompleteInstance = new window.google.maps.places.Autocomplete(
            addressInput,
            { 
              types: ["geocode"],
              componentRestrictions: { country: "ae" },
              fields: ["address_components", "geometry", "formatted_address"]
            }
          );

          autocompleteInstance.addListener("place_changed", () => {
            const place = autocompleteInstance.getPlace();
            if (!place.geometry) return;

            // Update map and marker
            newMap.setCenter(place.geometry.location);
            newMarker.setPosition(place.geometry.location);

            // Parse address components
            const addressComponents = parseAddressComponents(place.address_components);
            
            // Update form data
            setFormData(prev => ({
              ...prev,
              address: place.formatted_address,
              area: addressComponents.neighborhood || addressComponents.sublocality || "",
              street: addressComponents.route || "",
              city: addressComponents.locality || "Dubai",
              country: addressComponents.country || "UAE",
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            }));
          });
        }

        setMap(newMap);
        setMarker(newMarker);
        autocompleteRef.current = autocompleteInstance;

        // Reverse geocode the initial position
        reverseGeocode(center);

      } catch (error) {
        console.error("Map initialization error:", error);
        toast.error("Failed to load maps");
      }
    };

    initMap();

    return () => {
      isMounted = false;
      if (autocompleteInstance) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteInstance);
      }
      if (marker) marker.setMap(null);
    };
  }, [locationDetermined]); // This effect runs when locationDetermined changes

  // Helper function to parse address components
  const parseAddressComponents = (components) => {
    const result = {};
    components.forEach(component => {
      if (component.types.includes("route")) result.route = component.long_name;
      if (component.types.includes("sublocality") || component.types.includes("neighborhood")) {
        result.neighborhood = component.long_name;
      }
      if (component.types.includes("locality")) result.locality = component.long_name;
      if (component.types.includes("administrative_area_level_1")) result.administrative_area = component.long_name;
      if (component.types.includes("country")) result.country = component.long_name;
    });
    return result;
  };

  // Reverse geocode function
  const reverseGeocode = (latLng) => {
    if (!window.google) return;
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const addressComponents = parseAddressComponents(results[0].address_components);
        
        setFormData(prev => ({
          ...prev,
          address: results[0].formatted_address,
          area: addressComponents.neighborhood || addressComponents.sublocality || "",
          street: addressComponents.route || "",
          city: addressComponents.locality || addressComponents.administrative_area || "Dubai",
          country: addressComponents.country || "UAE",
          lat: latLng.lat,
          lng: latLng.lng
        }));
        
        const addressInput = document.getElementById("address-input");
        if (addressInput) {
          addressInput.value = results[0].formatted_address;
        }
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.area) {
        toast.error('Enter Your Area');
        return false;
      }
      setIsLoader(true);
      
      let url;
      if (address) {
        url = `/updateAddress`;
        formData.address_id = address?._id;
      } else {
        url = `/addAddress`;
      }
      if(!formData.villa_apt_no){
        formData.villa_apt_no = formData.area;
      }
      
      const response = await ApiService.request({
        method: "POST",
        url,
        data: formData
      });
      
      const data = response.data;
      if (data.status) {
        toast.success(data.message);
        if (authUser.is_guest) {
          localStorage.setItem("address", JSON.stringify(data.data));
          // If there's a goto parameter, navigate to that page, otherwise go to home
          if (goto) {
            navigate('/' + goto);
          } else {
            navigate('/');
          }
          return true;
        }
        navigate('/address-list?goto='+goto);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Failed to save address");
    } finally {
      setIsLoader(false);
    }
  };

  if (isPageLoading) {
    return <PageLoader />;
  }

  return (
    <div className="main-heading items-details-main container">
      <Header 
        title={id ? 'Edit Address' : 'Add New Address'} 
        backUrl="/address-list"
      />

      <div className="row">
        <div className="col-md-6">
          {/* Map Container with Location Button */}
          <div style={{ position: 'relative' }}>
            <div 
              ref={mapRef}
              style={{ 
                height: "300px", 
                width: "100%",
                backgroundColor: "#e0e0e0",
                borderRadius: "8px"
              }}
            />
            {/* Current Location Button */}
            <button
              onClick={getUserCurrentLocation}
              disabled={isGettingLocation}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                cursor: 'pointer',
                zIndex: 1000
              }}
              title="Use my current location"
            >
              {isGettingLocation ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <MdMyLocation />
              )}
            </button>
            
            {/* Location permission denied message */}
            {locationPermissionDenied && (
              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '10px',
                right: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                padding: '8px',
                borderRadius: '4px',
                fontSize: '12px',
                textAlign: 'center',
                zIndex: 1000
              }}>
                Location access denied. Click the location button to try again.
              </div>
            )}
          </div>
        </div>
        
        <div className="col-md-6">
            {/* Address Form */}
            <form onSubmit={handleSubmit} className="form-container p-3">
              {/* Location Search */}
              <div className="mb-3">
                <label className="form-label">Location*</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <MdLocationPin />
                  </span>
                  <input
                    id="address-input" required
                    type="text"
                    className="form-control"
                    placeholder="Search for your area"
                    value={formData?.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                  />
                </div>
                <div className="mt-2 text-muted">
                  <small>{formData.city}, {formData.country}</small>
                </div>
              </div>

              {/* Address Type Selection */}
              {!authUser.is_guest && 
                <div className="address-type mb-3">
                  <button
                    type="button"
                    className={`type-btn ${formData.type === "home" ? "active" : ""}`}
                    onClick={() => setFormData({...formData, type: "home"})}
                  >
                    <MdHome /> Home
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${formData.type === "work" ? "active" : ""}`}
                    onClick={() => setFormData({...formData, type: "work"})}
                  >
                    <MdWork /> Work
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${formData.type === "other" ? "active" : ""}`}
                    onClick={() => setFormData({...formData, type: "other"})}
                  >
                    <MdApartment /> Other
                  </button>
                </div>
              }

              <div className="mb-3">
                <label className="form-label">Area*</label>
                <input
                  type="text"
                  className="form-control"
                  name="area"
                  placeholder="Area/district"
                  value={formData.area}
                  onChange={handleInputChange}
                />
              </div>

              {!authUser.is_guest && 
              <>
                {/* Address Details */}
                <div className="mb-3">
                  <label className="form-label">Apartment/Villa No.</label>
                  <input
                    type="text"
                    className="form-control"
                    name="villa_apt_no"
                    placeholder="Apartment or villa number"
                    value={formData.villa_apt_no}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label">Floor</label>
                    <input
                      type="text"
                      className="form-control"
                      name="floor"
                      placeholder="Floor number"
                      value={formData.floor}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Street No</label>
                    <input
                      type="text"
                      className="form-control"
                      name="street"
                      placeholder="Street number"
                      value={formData.street}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-check mb-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="primary-address"
                    name="is_default"
                    checked={formData.is_default}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label" htmlFor="primary-address">
                    Set as primary address
                  </label>
                </div>
              </>
              }
              {locationPermissionDenied ?(
                <span className="text-danger">Please enable location permission to use this feature.</span>
              ):(
                <button 
                  type="submit" 
                  className="btn btn-base w-100 py-3 m-0"
                  disabled={isLoader}
                >
                  {isLoader ? 'Saving...' : (!authUser.is_guest)?'Save Address':'Continue'}
                </button>
              )}
              
            </form>
        </div>
      </div>
    </div>
  );
};

export default AddAddress;